import { useEffect, useRef, useState, type ReactNode } from 'react';
import { uiFormat, uiText } from '../../i18n';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import type { StudioForm } from '../studio/studio-api';
import { PhotoCrop } from '../photo/PhotoCrop';
import { useVideoSourcePreview } from './use-video-source-preview';
import { StudioIcon } from '../studio/StudioIcon';
import { StudioSheet } from '../studio/StudioSheet';
import { StudioUploadRights } from '../studio/StudioUploadRights';
import { StudioUploadExample, uploadLimits, type StudioUploadExampleData } from '../studio/StudioUploadExample';

export function VideoSource({
  field,
  state,
  crop = true,
  trim,
  captureMode = 'environment',
  cropRatio,
  title,
  uploadLabel,
  uploadId,
  onVideoMetadata,
  mediaFields,
  onMediaFile,
  queuedFile,
  onQueuedFileConsumed,
  badge,
  subtitle,
  limits,
  example,
  heading,
}: {
  field: StudioForm['fields'][number];
  state: CircleFormState;
  crop?: boolean;
  trim?: { minMs: number; maxMs: number };
  captureMode?: 'environment' | 'user';
  cropRatio?: string;
  title?: string;
  uploadLabel?: string;
  uploadId?: string;
  onVideoMetadata?(value: { width: number; height: number; durationMs: number }): void;
  mediaFields?: StudioForm['fields'];
  onMediaFile?(field: StudioForm['fields'][number], file: File): void;
  queuedFile?: File | null;
  onQueuedFileConsumed?(): void;
  badge?: ReactNode;
  /** Подпись экрана в пустой рамке («Селфи, портрет или любой снимок») */
  subtitle?: string;
  /** Ограничения одной строкой; по умолчанию — из accept и max_bytes поля («JPG или PNG, до 10 МБ») */
  limits?: string;
  /** Пример «Было · Стало» в пустой рамке у пресетов с одним стилем (§1.3), только из ответа сервера */
  example?: StudioUploadExampleData | null;
  /** Заголовок пустой рамки вместо «Загрузите фото» */
  heading?: string;
}) {
  const [duration, setDuration] = useState(0),
    [error, setError] = useState('');
  const [consentFile, setConsentFile] = useState<File | null>(null);
  const isVideo = field.upload?.kind === 'video';
  const maximum = field.upload?.max_bytes ?? (isVideo ? 100 : 20) * 1024 * 1024;
  const preview = useVideoSourcePreview(state.inputs[field.key], state.ownerId, isVideo ? 'video' : 'photo', maximum);
  const url = preview.url;
  const consumed = useRef<File | null>(null);
  const clear = () => {
    state.clearUpload(field.key);
    preview.select(null);
    setDuration(0);
    state.setInputs(current => {
      const next = { ...current };
      for (const key of isVideo ? ['start_ms', 'end_ms', 'crop_x', 'crop_y', 'zoom'] : ['crop_x', 'crop_y', 'zoom']) delete next[key];
      return next;
    });
  };
  const select = (file: File | undefined) => {
    if (!file) return;
    if (!state.rights) {
      setConsentFile(file);
      return;
    }
    setError('');
    const target = mediaFields?.find(candidate => candidate.upload?.accept.includes(file.type));
    if (target && target.key !== field.key && onMediaFile) {
      const targetMaximum = target.upload?.max_bytes ?? (target.upload?.kind === 'video' ? 100 : 20) * 1024 * 1024;
      if (file.size > targetMaximum) {
        setError(uiFormat('Файл слишком большой. Максимум {0} МБ', targetMaximum / 1024 / 1024));
        return;
      }
      onMediaFile(target, file);
      return;
    }
    if (file.size > maximum) {
      setError(uiFormat('Файл слишком большой. Максимум {0} МБ', maximum / 1024 / 1024));
      return;
    }
    if (!field.upload!.accept.includes(file.type)) {
      setError('Выберите файл подходящего формата');
      return;
    }
    preview.select(file);
    void state.upload(field.key, file);
  };
  useEffect(() => {
    if (!queuedFile || consumed.current === queuedFile || state.locked || !state.rights) return;
    consumed.current = queuedFile;
    select(queuedFile);
    onQueuedFileConsumed?.();
    // Один явный выбор файла продолжает загрузку после переключения его фактического вида.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queuedFile, state.locked, state.rights]);
  const pending = !!state.uploadIds[field.key] && !state.inputs[field.key];
  const trimAvailable = ['start_ms', 'end_ms'].every(key => state.form?.fields.some(f => f.key === key));
  if (!field.upload) return null;
  return (
    <fieldset className="video-source studio-native-upload">
      <legend className="sr-only">{uiText(title ?? (isVideo ? 'Исходное видео' : 'Первый кадр'))}</legend>
      <div className={`bcu${url ? ' has' : ''}${example && !state.uploadIds[field.key] ? ' ex' : ''}`}>
        {!state.uploadIds[field.key] && example && <StudioUploadExample example={example} />}
        {!state.uploadIds[field.key] && (
          <div className="bcu-empty">
            <span className="bcu-ic">
              <StudioIcon kind={mediaFields?.length ? 'video' : isVideo ? 'video' : 'photo'} />
            </span>
            <b className="bcu-t">
              {uiText(
                example?.title ?? heading ?? (mediaFields?.length ? 'Загрузите фото или видео' : isVideo ? 'Загрузите видео' : 'Загрузите фото'),
              )}
            </b>
            {(example?.text ?? subtitle ?? (mediaFields?.length ? 'Фото оживим, видео изменим' : '')) && (
              <span className="bcu-s">{uiText(example?.text ?? subtitle ?? 'Фото оживим, видео изменим')}</span>
            )}
            <div className="bcu-acts">
              <label className="bcu-pl pri">
                {uiText('Загрузить')}
                <input
                  aria-label={uiText(
                    uploadLabel ?? (mediaFields?.length ? 'Загрузить фото или видео' : isVideo ? 'Загрузить видео' : 'Загрузить фото'),
                  )}
                  type="file"
                  className="studio-native-file-input"
                  id={uploadId}
                  accept={(mediaFields?.length
                    ? [...new Set(mediaFields.flatMap(candidate => candidate.upload?.accept ?? []))]
                    : field.upload.accept
                  ).join(',')}
                  disabled={state.locked}
                  onChange={e => {
                    select(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
              </label>
              <label className="bcu-pl">
                {uiText('Снять')}
                <input
                  aria-label={uiText(mediaFields?.length ? 'Снять фото или видео' : isVideo ? 'Снять видео' : 'Снять фото')}
                  type="file"
                  className="studio-native-file-input"
                  capture={captureMode}
                  accept={mediaFields?.length ? 'image/*,video/*' : isVideo ? 'video/*' : 'image/*'}
                  disabled={state.locked}
                  onChange={e => {
                    select(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
            <small className="bcu-n">
              {limits ? uiText(limits) : mediaFields?.length
                ? mediaFields
                    .map(candidate =>
                      uiFormat(
                        candidate.key === 'video' ? 'Видео до {0} МБ' : 'Фото до {0} МБ',
                        (candidate.upload?.max_bytes ?? 10 * 1024 * 1024) / 1024 / 1024,
                      ),
                    )
                    .join(', ')
                : uploadLimits(field)}
            </small>
          </div>
        )}
        {badge}
        {preview.loading && <p role="status">{uiText('Загружаем…')}</p>}
        {preview.error && (
          <p role="alert">
            {uiText('Не удалось прочитать файл.')}{' '}
            <button type="button" onClick={preview.retry}>
              {uiText('Повторить')}
            </button>
          </p>
        )}
        {url &&
          (isVideo ? (
            <video
              controls
              playsInline
              preload="metadata"
              src={url}
              onPlay={e => {
                const start = Number(state.inputs.start_ms ?? 0) / 1000,
                  end = Number(state.inputs.end_ms) / 1000;
                if (
                  Number.isFinite(start) &&
                  Number.isFinite(end) &&
                  end > start &&
                  (e.currentTarget.currentTime < start || e.currentTarget.currentTime >= end)
                )
                  e.currentTarget.currentTime = start;
              }}
              onTimeUpdate={e => {
                const end = Number(state.inputs.end_ms) / 1000;
                if (!e.currentTarget.paused && Number.isFinite(end) && end > 0 && e.currentTarget.currentTime >= end) {
                  e.currentTarget.pause();
                  e.currentTarget.currentTime = end;
                }
              }}
              onLoadedMetadata={e => {
                const ms = Math.floor(e.currentTarget.duration * 1000);
                if (!Number.isFinite(ms) || ms < (trim?.minMs ?? 1000)) {
                  setError(
                    trim
                      ? uiFormat('Видео должно длиться не меньше {0} секунд', trim.minMs / 1000)
                      : 'Видео должно длиться не меньше секунды',
                  );
                  return;
                }
                setDuration(ms);
                onVideoMetadata?.({ width: e.currentTarget.videoWidth, height: e.currentTarget.videoHeight, durationMs: ms });
                if (trimAvailable)
                  state.setInputs(v => ({
                    ...v,
                    start_ms: v.start_ms ?? '0',
                    end_ms: v.end_ms ?? String(Math.min(ms, trim?.maxMs ?? 10000)),
                  }));
              }}
            />
          ) : (
            <>
              <img alt={uiText('Первый кадр')} src={url} />
              {crop && (
                <div className="bcu-acts bcu-caps">
                  <PhotoCrop url={url} state={cropRatio ? { ...state, inputs: { ...state.inputs, format: cropRatio } } : state} />
                </div>
              )}
            </>
          ))}
      </div>
      {duration > 0 && <p>{uiFormat('Длительность исходника: {0} с', (duration / 1000).toFixed(1))}</p>}
      {isVideo && trimAvailable && (duration > 0 || state.inputs.video) && (
        <fieldset className="studio-native-trim">
          <legend>
            {trim ? uiFormat('Фрагмент от {0} до {1} секунд', trim.minMs / 1000, trim.maxMs / 1000) : uiText('Фрагмент от 1 до 10 секунд')}
          </legend>
          {(['start_ms', 'end_ms'] as const).map(key => (
            <label key={key}>
              {uiText(key === 'start_ms' ? 'Начало, секунды' : 'Конец, секунды')}
              <input
                type="number"
                min={0}
                max={duration ? duration / 1000 : undefined}
                step={0.1}
                disabled={state.locked}
                value={Number(state.inputs[key] ?? (key === 'end_ms' ? 10000 : 0)) / 1000}
                onChange={e => state.setInputs(v => ({ ...v, [key]: String(Math.round(Number(e.target.value) * 1000)) }))}
              />
            </label>
          ))}
        </fieldset>
      )}
      {state.inputs[field.key] && <p role="status">{uiText('Загружено')}</p>}
      {pending && (
        <button type="button" disabled={state.busy || state.locked || !state.rights} onClick={() => void state.upload(field.key, null)}>
          {uiText('Продолжить загрузку')}
        </button>
      )}
      {state.uploadIds[field.key] && (
        <>
          <button className="bcu-pl" type="button" disabled={state.locked} onClick={clear}>
            {uiText('Заменить файл')}
          </button>
          <button className="bcu-pl" type="button" disabled={state.locked} onClick={clear}>
            {uiText('Убрать')}
          </button>
        </>
      )}
      {error && <p role="alert">{uiText(error)}</p>}
      <p className="bc-cap">
        <StudioIcon kind="photo" />
        <span>{uiText('Только свои файлы или с согласия владельца')}</span>
      </p>
      {consentFile && (
        <StudioSheet title="Права на материалы" onClose={() => setConsentFile(null)}>
          <h2>{uiText('Права на материалы')}</h2>
          <StudioUploadRights state={state} />
          <button
            type="button"
            className="btn"
            disabled={state.locked || !state.rights}
            onClick={() => {
              const file = consentFile;
              setConsentFile(null);
              select(file);
            }}
          >
            {uiText('Продолжить загрузку')}
          </button>
        </StudioSheet>
      )}
    </fieldset>
  );
}
